import { useState } from 'react';
import { useData, useList, useSession } from '@yingbaba/api';
import { Select, ActionDialog, Notice, PageTitle, QueryState, Pagination } from '@yingbaba/ui';
import { datetime, reviewScore } from '@yingbaba/utils';
import type { ReviewCaseView } from '@yingbaba/types';

const labels: Record<string, string> = {
  awaiting_response: '待评价方说明',
  ready: '待平台处理',
  appealed: '复核中',
  resolved: '已处理',
  closed: '复核已完成',
};
export function ReviewModeration() {
  const [filter, setFilter] = useState('active');
  const [page, setPage] = useState(1);
  const session = useSession();
  const role = session.data?.account.platform_role || '';
  const canResolve = ['super_admin', 'operator', 'reviewer', 'customer_service'].includes(role);
  const q = useList<ReviewCaseView>(
    `/admin/review-cases?status=${filter}&page=${page}&page_size=20`,
  );
  return (
    <>
      <PageTitle title="评价申诉" description="查看双方说明、记录处理依据，并在需要时完成复核。" />
      <Notice>
        低分本身不是移除依据。停止公开展示后，该评价及回应退出公开页面和评分，项目内仍保留原始记录。
      </Notice>
      <section className="panel stack" style={{ marginTop: 24 }}>
        <div className="panel-title review-queue-title">
          <h3>处理队列</h3>
          <Select
            aria-label="评价申诉状态"
            value={filter}
            onChange={(e) => {
              setFilter(e);
              setPage(1);
            }}
          >
            {[
              ['active', '待处理与复核'],
              ['', '全部记录'],
              ['resolved', '已处理'],
              ['closed', '复核已完成'],
            ].map(([value, label]) => (
              <option value={value} key={value}>
                {label}
              </option>
            ))}
          </Select>
        </div>
        <QueryState query={q} empty={q.data?.total === 0}>
          {q.data?.items.map((item) => (
            <article className="panel stack" key={item.id}>
              <div className="row spread wrap">
                <h3>{item.project_title}</h3>
                <span className="tag">{labels[item.status] || '处理中'}</span>
              </div>
              <p className="small muted">
                {datetime(item.created_at)} · 项目 {item.project_id.slice(-8)}
              </p>
              <div className="stack">
                <strong>原评价 · {reviewScore(item.review)} / 5</strong>
                <p className="prose">{item.review.content}</p>
                {item.review.reply && <p className="prose">公开回应：{item.review.reply}</p>}
              </div>
              <p className="prose">申诉说明：{item.reason}</p>
              <p className="prose">
                评价方说明：{item.statement || '尚未提交；处理时请核查已有项目记录和双方事实。'}
              </p>
              <ReviewEvidence id={item.id} />
              {item.resolution && (
                <Notice>
                  首次处理：{item.decision === 'hide' ? '停止公开展示' : '保留展示'} ·{' '}
                  {item.resolution}
                </Notice>
              )}
              {item.appeal_reason && <p className="prose">复核依据：{item.appeal_reason}</p>}
              {item.appeal_resolution && (
                <Notice>
                  复核结果：{item.appeal_decision === 'hide' ? '停止公开展示' : '保留展示'} ·{' '}
                  {item.appeal_resolution}
                </Notice>
              )}
              {canResolve && ['awaiting_response', 'ready', 'appealed'].includes(item.status) && (
                <ActionDialog
                  title={item.status === 'appealed' ? '复核评价处理' : '处理评价申诉'}
                  trigger={item.status === 'appealed' ? '处理复核' : '处理申诉'}
                  path={`/admin/review-cases/${item.id}/resolve`}
                  submitLabel="记录结论并更新展示"
                  description="保留原始评分和双方说明，不改写评价内容。处理依据会通知双方；不允许处理自己组织的交易。"
                  fields={[
                    {
                      name: 'decision',
                      label: '处理结果',
                      value: '',
                      options: [
                        { value: '', label: '请选择处理结果' },
                        { value: 'retain', label: '保留评价及公开评分' },
                        { value: 'hide', label: '停止公开展示并退出评分' },
                      ],
                    },
                    {
                      name: 'resolution',
                      label: '核查事实与处理依据',
                      type: 'textarea',
                      min: 10,
                      max: 3000,
                    },
                  ]}
                />
              )}
            </article>
          ))}
        </QueryState>
        <Pagination
          page={page}
          size={q.data?.page_size || 20}
          total={q.data?.total || 0}
          onPage={setPage}
        />
      </section>
    </>
  );
}

function ReviewEvidence({ id }: { id: string }) {
  const [open, setOpen] = useState(false);
  const q = useData<{
    project_title: string;
    project_status: string;
    order_status: string;
    deliveries: { title: string; version: number; status: string; feedback: string }[];
    criteria: { title: string; description: string; status: string }[];
  }>(`/admin/review-cases/${id}/evidence`, open);
  return (
    <details onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary>核查关联交易与验收记录</summary>
      {open && (
        <QueryState query={q}>
          <div className="stack" style={{ marginTop: 16 }}>
            <p>
              成交订单：{q.data?.order_status === 'completed' ? '已完成' : '尚未完成'} · 项目：
              {q.data?.project_title}
            </p>
            {q.data?.criteria.map((c, index) => (
              <div key={index}>
                <strong>{c.title}</strong>
                <p className="prose small">{c.description}</p>
              </div>
            ))}
            {q.data?.deliveries.map((d, index) => (
              <div key={index}>
                <strong>
                  {d.title} · V{d.version} ·{' '}
                  {d.status === 'accepted'
                    ? '已验收'
                    : d.status === 'revision'
                      ? '要求修改'
                      : '已提交'}
                </strong>
                {d.feedback && <p className="small prose">验收反馈：{d.feedback}</p>}
              </div>
            ))}
            <p className="small muted">
              查看操作记录在审计日志中；会话访问继续由客服按工单显式加入。
            </p>
          </div>
        </QueryState>
      )}
    </details>
  );
}
